import {
    FaRightFromBracket,
    FaChevronDown,
    FaXmark,
    FaAnglesLeft,
    FaAnglesRight,
    FaSpinner,
} from "react-icons/fa6";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Logo from "../../../assets/images/logo_radius_trend.png";
import { AuthService } from "../../../services/AuthService";
import { FlyoutPanel } from "./components/FlyoutPanel";
import { NavLinkItemSuperAdmin } from "./navlink/NavlinkSuperAdmin";
import { AccordionContent } from "./components/AccordingSidebarContent";
import { Tooltip } from "./components/Tooltip";
import { NavLinkItemTeknisi } from "./navlink/NavlinkTeknisi";
import { NavLinkItemMitra } from "./navlink/NavlinkMitra";
import { NavLinkItemReseller } from "./navlink/NavlinkReseller";
import { NavLinkItemAbo } from "./navlink/NavlinkAbo";
import { NavLinkItemOwnerApp } from "./navlink/NavlinkOwnerApp";


export const Sidebar = ({ showSidebar, setShowSidebar }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [openAccordions, setOpenAccordions] = useState({});
    const [flyout, setFlyout] = useState(null);
    const [loadingLogout, setLoadingLogout] = useState(false);
    const [navItems, setNavItems] = useState([]);

    const isExpanded = showSidebar;
    const isMini = !showSidebar;

    const isActive = (path) => location.pathname === path;

    const isAccordionActive = (item) =>
        item.type === "accordion" && item.subItems?.some((sub) => isActive(sub.path));


    const [token, setToken] = useState(null);
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        const t = localStorage.getItem('token-radiustrend');
        const u = localStorage.getItem('user_data-radiustrend');
        if (!t || !u) {
            navigate('/login');
        }
    }, []);

    /* ============================================== */
    /* RESPONSIVE: Desktop = Open, Mobile = Hide       */
    /* ============================================== */
    useEffect(() => {
        // Gunakan matchMedia untuk performa lebih baik
        const mql = window.matchMedia("(min-width: 1024px)");

        const handleChange = (e) => {
            if (e.matches) {
                // Desktop: buka sidebar
                setShowSidebar(true);
            } else {
                // Mobile/Tablet: tutup sidebar
                setShowSidebar(false);
            }
        };

        // Set initial state berdasarkan ukuran layar saat ini
        handleChange(mql);

        // Listen perubahan ukuran layar
        mql.addEventListener("change", handleChange);
        return () => mql.removeEventListener("change", handleChange);
    }, [setShowSidebar]);

    /* ============================================== */

    const handleNavigate = () => {
        if (window.innerWidth < 1024) {
            setShowSidebar(false);
        }
    };

    // Load navigasi berdasarkan role
    useEffect(() => {
        try {
            const user = JSON.parse(localStorage.getItem("user_data-radiustrend"));
            switch (user?.role?.name) {
                case "owner app":
                    setNavItems(NavLinkItemOwnerApp);
                    break;
                case "admin":
                case "super admin":
                    if (user?.email === "express@gmail.com") {
                        setNavItems(NavLinkItemAbo);
                    } else {
                        setNavItems(NavLinkItemSuperAdmin);
                    }
                    break;
                case "teknisi":
                    setNavItems(NavLinkItemTeknisi);
                    break;
                case "mitra":
                    setNavItems(NavLinkItemMitra);
                    break;
                case "reseller":
                    setNavItems(NavLinkItemReseller);
                    break;
                default:
                    setNavItems(NavLinkItemSuperAdmin);
                    break;
            }
        } catch (error) {
            console.warn("Gagal load navigasi:", error);
            setNavItems(NavLinkItemSuperAdmin);
        }
    }, []);

    // Auto-open accordion yang punya sub-item aktif
    useEffect(() => {
        navItems.forEach((item, index) => {
            if (item.type === "accordion" && isAccordionActive(item)) {
                setOpenAccordions((prev) => ({ ...prev, [index]: true }));
            }
        });
    }, [location.pathname, navItems]);

    // Tutup flyout saat berpindah halaman
    useEffect(() => {
        setFlyout(null);
    }, [location.pathname]);

    // Tutup flyout saat sidebar di-expand
    useEffect(() => {
        if (isExpanded) setFlyout(null);
    }, [isExpanded]);

    const toggleAccordion = (itemId) => {
        setOpenAccordions((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
    };

    const handleMiniAccordionClick = (item, index) => {
        if (flyout?.id === index) {
            setFlyout(null);
        } else {
            setFlyout({ id: index, item });
        }
    };

    const handleLogout = async (e) => {
        e.preventDefault();
        setLoadingLogout(true);
        try {
            await AuthService.logout({ access_token: localStorage.getItem("token") });
            localStorage.removeItem("token-radiustrend");
            localStorage.removeItem("token_type-radiustrend");
            localStorage.removeItem("user_data-radiustrend");
            navigate("/");
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingLogout(false);
        }
    };

    return (
        <aside
            className={`fixed top-0 left-0 z-40 h-full flex flex-col transition-all duration-300 ease-out
            ${isExpanded ? "w-64 translate-x-0" : "w-[72px] -translate-x-full lg:translate-x-0"}
          `}
            style={{
                background: "linear-gradient(180deg, #0B1426 0%, #060D1F 100%)",
                borderRight: isExpanded
                    ? "1px solid rgba(0, 180, 216, 0.08)"
                    : "1px solid rgba(51, 65, 85, 0.2)",
                boxShadow: isExpanded
                    ? "4px 0 30px rgba(0, 0, 0, 0.3)"
                    : "2px 0 15px rgba(0, 0, 0, 0.2)",
            }}
            aria-label="Sidebar navigasi"
        >
            {/* ===== Top Bar: Logo + Toggle ===== */}
            <div
                className="flex items-center justify-between flex-shrink-0 h-16 px-3"
                style={{
                    borderBottom: isExpanded
                        ? "1px solid rgba(0, 180, 216, 0.08)"
                        : "1px solid rgba(51, 65, 85, 0.2)",
                }}
            >
                {isExpanded ? (
                    <Link to="/dashboard-menu" className="flex items-center gap-3 min-w-0">
                        <div
                            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{
                                background: "linear-gradient(135deg, rgba(0,180,216,0.2), rgba(0,119,182,0.1))",
                                border: "1px solid rgba(0,180,216,0.2)",
                                boxShadow: "0 4px 12px rgba(0,180,216,0.1)",
                            }}
                        >
                            <img src={Logo} alt="Logo" className="w-5 h-5 object-contain" />
                        </div>
                        <h1 className="font-display font-bold text-base text-white leading-tight truncate">
                            Radius<span className="text-ocean-500">Trend</span>
                        </h1>
                    </Link>
                ) : (
                    <Link
                        to="/dashboard-menu"
                        className="mx-auto w-9 h-9 rounded-xl flex items-center justify-center"
                        style={{
                            background: "linear-gradient(135deg, rgba(0,180,216,0.2), rgba(0,119,182,0.1))",
                            border: "1px solid rgba(0,180,216,0.2)",
                        }}
                    >
                        <img src={Logo} alt="Logo" className="w-5 h-5 object-contain" />
                    </Link>
                )}

                {/* Close: mobile only */}
                {isExpanded && (
                    <button
                        onClick={() => setShowSidebar(false)}
                        className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all flex-shrink-0"
                        aria-label="Tutup sidebar"
                    >
                        <FaXmark className="text-sm" />
                    </button>
                )}
            </div>

            {/* ===== Toggle Button (desktop only) ===== */}
            <div className="flex-shrink-0 px-3 pt-3 lg:block hidden">
                <button
                    onClick={() => setShowSidebar(!showSidebar)}
                    className="w-full flex items-center justify-center py-2 rounded-lg text-slate-500 hover:text-ocean-400 hover:bg-ocean-600/8 transition-all duration-200"
                    aria-label={isExpanded ? "Perkecil sidebar" : "Perbesar sidebar"}
                >
                    {isExpanded ? (
                        <FaAnglesLeft className="text-sm" />
                    ) : (
                        <FaAnglesRight className="text-sm" />
                    )}
                </button>
            </div>

            {/* ===== Navigation ===== */}
            <nav
                className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-3 space-y-0.5 relative"
                aria-label="Menu utama"
            >
                {navItems.map((item, index) => {
                    const active = item.type === "link" ? isActive(item.path) : isAccordionActive(item);
                    const Icon = item.icon;

                    /* ---------- LINK ITEM ---------- */
                    if (item.type === "link") {
                        const linkContent = (
                            <>
                                <div
                                    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${active
                                            ? "bg-ocean-600/20 border border-ocean-500/30"
                                            : "border border-transparent"
                                        }`}
                                >
                                    <Icon
                                        className={`text-base transition-colors duration-200 ${active ? "text-ocean-400" : "text-slate-500 group-hover:text-slate-300"
                                            }`}
                                    />
                                </div>
                                {isExpanded && (
                                    <span
                                        className={`text-sm font-medium transition-colors duration-200 truncate ${active ? "text-ocean-400" : "text-slate-300 group-hover:text-white"
                                            }`}
                                    >
                                        {item.name}
                                    </span>
                                )}
                            </>
                        );

                        if (isMini) {
                            return (
                                <Tooltip key={index} label={item.name}>
                                    <Link
                                        to={item.path}
                                        onClick={handleNavigate}
                                        className="group flex items-center justify-center w-full p-2.5 rounded-xl transition-all duration-200 hover:bg-slate-800/60 relative"
                                    >
                                        {linkContent}
                                    </Link>
                                </Tooltip>
                            );
                        }

                        return (
                            <Link
                                key={index}
                                to={item.path}
                                onClick={handleNavigate}
                                className="group flex items-center gap-3 w-full p-3 rounded-xl transition-all duration-200 relative hover:bg-slate-800/60"
                            >
                                {active && (
                                    <div
                                        className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-7 rounded-r-full bg-ocean-500"
                                        style={{ boxShadow: "0 0 8px rgba(0, 180, 216, 0.4)" }}
                                    />
                                )}
                                {linkContent}
                            </Link>
                        );
                    }

                    /* ---------- ACCORDION ITEM ---------- */
                    if (item.type === "accordion") {
                        const isOpen = openAccordions[index] || false;
                        const isFlyoutOpen = flyout?.id === index;

                        // === MINI MODE ===
                        if (isMini) {
                            return (
                                <div key={index} className="relative">
                                    <Tooltip label={item.name}>
                                        <button
                                            onClick={() => handleMiniAccordionClick(item)}
                                            className={`group flex items-center justify-center w-full p-2.5 rounded-xl transition-all duration-200 hover:bg-slate-800/60 relative ${isFlyoutOpen ? "bg-slate-800/60" : ""
                                                }`}
                                        >
                                            <div
                                                className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${active
                                                        ? "bg-ocean-600/20 border border-ocean-500/30"
                                                        : "border border-transparent"
                                                    }`}
                                            >
                                                <Icon
                                                    className={`text-base transition-colors duration-200 ${active ? "text-ocean-400" : "text-slate-500 group-hover:text-slate-300"
                                                        }`}
                                                />
                                            </div>
                                            {active && (
                                                <span
                                                    className="absolute top-2 right-2 w-2 h-2 rounded-full bg-ocean-500"
                                                    style={{ boxShadow: "0 0 6px rgba(0, 180, 216, 0.5)" }}
                                                />
                                            )}
                                        </button>
                                    </Tooltip>

                                    {isFlyoutOpen && (
                                        <FlyoutPanel
                                            item={item}
                                            isActive={isActive}
                                            onNavigate={handleNavigate}
                                            onClose={() => setFlyout(null)}
                                        />
                                    )}
                                </div>
                            );
                        }

                        // === EXPANDED MODE ===
                        return (
                            <div key={index}>
                                <button
                                    onClick={() => toggleAccordion(index)}
                                    className="group flex items-center justify-between w-full gap-3 p-3 rounded-xl transition-all duration-200 relative hover:bg-slate-800/60"
                                >
                                    {active && (
                                        <div
                                            className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-7 rounded-r-full bg-ocean-500"
                                            style={{ boxShadow: "0 0 8px rgba(0, 180, 216, 0.4)" }}
                                        />
                                    )}
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div
                                            className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${active
                                                    ? "bg-ocean-600/20 border border-ocean-500/30"
                                                    : "border border-transparent"
                                                }`}
                                        >
                                            <Icon
                                                className={`text-base transition-colors duration-200 ${active ? "text-ocean-400" : "text-slate-500 group-hover:text-slate-300"
                                                    }`}
                                            />
                                        </div>
                                        <span
                                            className={`text-sm font-medium transition-colors duration-200 truncate ${active ? "text-ocean-400" : "text-slate-300 group-hover:text-white"
                                                }`}
                                        >
                                            {item.name}
                                        </span>
                                    </div>
                                    <FaChevronDown
                                        className={`text-xs transition-transform duration-300 ease-out flex-shrink-0 ${isOpen ? "rotate-180 text-ocean-400" : "text-slate-600"
                                            }`}
                                    />
                                </button>

                                <AccordionContent
                                    subItems={item.subItems}
                                    isOpen={isOpen}
                                    isActive={isActive}
                                    onNavigate={handleNavigate}
                                />
                            </div>
                        );
                    }

                    return null;
                })}
            </nav>

            {/* ===== Logout ===== */}
            <div
                className="flex-shrink-0 p-2.5"
                style={{
                    borderTop: isExpanded
                        ? "1px solid rgba(0, 180, 216, 0.08)"
                        : "1px solid rgba(51, 65, 85, 0.2)",
                }}
            >
                {isMini ? (
                    <Tooltip label="Logout">
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="group flex items-center justify-center w-full p-2.5 rounded-xl transition-all duration-200 hover:bg-red-500/10"
                            aria-label="Logout"
                        >
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center border border-transparent group-hover:border-red-500/20 transition-all">
                                {loadingLogout ? (
                                    <FaSpinner className="animate-spin text-white" />
                                ) : (
                                    <FaRightFromBracket className="text-base text-slate-500 group-hover:text-red-400 transition-colors" />
                                )}
                            </div>
                        </button>
                    </Tooltip>
                ) : (
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="group flex items-center justify-center gap-3 w-full p-3 rounded-xl transition-all duration-200 hover:bg-red-500/10"
                        aria-label="Logout"
                    >
                        {loadingLogout ? (
                            <FaSpinner className="animate-spin text-white" />
                        ) : (
                            <>
                                <div className="w-9 h-9 rounded-xl flex items-center justify-center border border-transparent group-hover:border-red-500/20 transition-all">
                                    <FaRightFromBracket className="text-base text-slate-500 group-hover:text-red-400 transition-colors" />
                                </div>
                                <span className="text-sm font-medium text-slate-400 group-hover:text-red-400 transition-colors">
                                    Logout
                                </span>
                            </>
                        )}
                    </button>
                )}
            </div>

            {/* ===== Styles ===== */}
            <style>{`
                /* Scrollbar */
                aside::-webkit-scrollbar { width: 3px; }
                aside::-webkit-scrollbar-track { background: transparent; }
                aside::-webkit-scrollbar-thumb { background: rgba(0,180,216,0.15); border-radius: 2px; }
                aside::-webkit-scrollbar-thumb:hover { background: rgba(0,180,216,0.3); }

                /* Flyout animation */
                @keyframes flyoutIn {
                    from { opacity: 0; transform: translateX(-8px) scale(0.96); }
                    to { opacity: 1; transform: translateX(0) scale(1); }
                }
            `}</style>
        </aside>
    );
};